import { isArray, mapValues, omit, zip } from 'lodash'
import moment from 'moment/moment'

import { DataType } from '../types/DataType'
import { ConstructorArgs } from '../types/models'
import { currencyFloat } from '../utils/numbers'
import { camelToSnake } from '../utils/strings'
import { ISerializable, isSerializable } from './ISerializable'
import type { Image } from './Image'
import { RecordDataMap } from './RecordDataMap'
import type { Source } from './Source'

type BaseRecordConstructorArgs = ConstructorArgs<() => BaseRecord>

export abstract class BaseRecord implements ISerializable {
    readonly iSerializable = true

    id?: string
    createdDate?: moment.Moment

    // This is how we currently store and recall any "foreign key" relations.
    // It is akin to the previous `lazyData` in that it's simply a mapping
    // from key to value, where the key is the name of the property, like "order"
    // or "owner", and the value is either an ID or the model instance itself.
    //
    // You shouldn't access `foreignKeyData` directly, but rather should use the methods
    // getForeignRecord and setForeignRecord. getForeignRecord will take care of
    // reifying a model if the stored value is actually an ID.
    protected foreignKeyData: RecordDataMap = new RecordDataMap()

    // This is a cache around the records we pull from foreignKeyData. This exists
    // because when we pull a record with the same key multiple times from foreignKeyData
    // we get a new instantiation of the record each time. If there are any React hooks
    // watching these records in their dependency arrays then those dependencies will change
    // on each render and cause the hooks to rerun, which leads to infinite loops in some
    // places in the code.
    private foreignKeyDataCache: { [key: string]: BaseRecord } = {}

    abstract recordType: string

    constructor(data?: Partial<BaseRecordConstructorArgs>) {
        if (!data) {
            return
        }

        this.id = data.id ?? undefined
        this.createdDate = this.propertyFromTimestampArg(data.created_date)
        this.foreignKeyData = new RecordDataMap(data)
    }

    protected propertyFromCurrencyArg(
        currencyArg: string | number | undefined | null
    ): number | undefined {
        const currencyNumber = this.propertyFromNumberArg(currencyArg)
        return currencyNumber !== undefined ? currencyFloat(currencyNumber) : undefined
    }

    protected propertyFromTimestampArg(
        timestampArg: moment.Moment | string | number | undefined | null
    ): moment.Moment | undefined {
        if (!timestampArg) {
            return undefined
        }

        if (typeof timestampArg === 'number') {
            return moment.unix(timestampArg)
        }

        if (typeof timestampArg === 'string') {
            return moment(timestampArg)
        }

        return timestampArg
    }

    protected propertyFromDateArg(
        dateArg: moment.Moment | string | number | undefined | null
    ): moment.Moment | undefined {
        if (!dateArg) {
            return undefined
        }

        if (moment.isMoment(dateArg)) {
            return dateArg
        }

        if (typeof dateArg === 'number') {
            return moment.unix(dateArg)
        }

        if (typeof dateArg !== 'string') {
            return undefined
        }

        const dateMoment = moment(dateArg, 'YYYY-MM-DD', true)
        if (dateMoment.isValid()) {
            return dateMoment
        }

        const dateTimeMoment = moment(dateArg, 'YYYY-MM-DDTHH:mm:ss', true)
        if (!dateTimeMoment.isValid()) {
            return undefined
        }

        return dateTimeMoment
    }

    protected propertyFromDictionaryArg(
        dictionaryArg: RecordDataMap | Map<any, any> | string | undefined | null
    ): RecordDataMap | undefined {
        if (!dictionaryArg) {
            return undefined
        }

        if (dictionaryArg instanceof RecordDataMap) {
            return dictionaryArg
        }

        if (dictionaryArg instanceof Map || typeof dictionaryArg === 'object') {
            return new RecordDataMap(dictionaryArg)
        }

        if (typeof dictionaryArg !== 'string') {
            return undefined
        }

        try {
            return new RecordDataMap(JSON.parse(dictionaryArg))
        }
        catch {
            return undefined
        }
    }

    protected propertyFromImageArg(
        imageArg: string | Image | undefined | null,
        imageURIArg: string | undefined | null
    ): Image | undefined {
        const Image = BaseRecord.getImageConstructor()

        if (!imageArg && !imageURIArg) {
            return undefined
        }

        if (imageArg instanceof Image) {
            return imageArg
        }

        if (imageArg) {
            const imageRecord = this.getForeignRecord(imageArg, Image)
            if (imageRecord !== undefined) {
                return imageRecord
            }
        }

        if (!imageURIArg) {
            return undefined
        }

        return new Image(undefined, {
            id: imageArg === null ? undefined : imageArg,
            uri: imageURIArg,
        })
    }

    protected propertyFromNumberArg(
        numberArg: string | number | undefined | null
    ): number | undefined {
        if (numberArg === undefined || numberArg === null || numberArg === '') {
            return undefined
        }
        return Number(numberArg)
    }

    protected propertyFromIntArg(intArg: string | number | undefined | null): number | undefined {
        if (intArg === undefined || intArg === null || intArg === '') {
            return undefined
        }
        return parseInt(Number(intArg).toString())
    }

    protected getForeignRecord<ModelT extends BaseRecord>(
        key: string,
        model: new (params: any) => ModelT
    ): ModelT | undefined {
        const value = this.foreignKeyData.get(key)
        if (!value) {
            return undefined
        }

        if (value instanceof BaseRecord) {
            return value as ModelT
        }

        if (this.foreignKeyDataCache[key]) {
            return this.foreignKeyDataCache[key] as ModelT
        }

        const record = this.foreignKeyData.valueForRecordKey(key, model) as ModelT | undefined
        if (record) {
            this.foreignKeyDataCache[key] = record
        }
        return record
    }

    protected setForeignRecord<ModelT extends BaseRecord>(key: string, model: ModelT | undefined) {
        this.foreignKeyData.put(key, model)
    }

    protected getForeignRecordID(key: string): string | undefined {
        const value = this.foreignKeyData.get(key)
        if (!value) {
            return undefined
        }

        if (value instanceof BaseRecord) {
            return value.id
        }

        if (typeof value === 'string') {
            return value
        }

        return undefined
    }

    protected getSourceRecord(key: string): Source | undefined {
        const Source = BaseRecord.getSourceConstructor()

        const source = this.getForeignRecord(key, Source)
        if (source) {
            return source
        }

        const sourceID = this.foreignKeyData.valueForKey(key)
        if (!sourceID) {
            return undefined
        }

        return Source.initWithId(sourceID)
    }

    protected serializeForClone(): Record<string, any> {
        const EXCLUDED_PROP_NAMES = ['iSerializable', 'foreignKeyData', 'recordType', 'superClass']

        const serializedProperties: Record<string, any> = {}

        let target = this
        while (target !== null) {
            for (const propName of Object.getOwnPropertyNames(target)) {
                if (EXCLUDED_PROP_NAMES.includes(propName)) {
                    continue
                }

                const propDescriptor = Object.getOwnPropertyDescriptor(target, propName)
                if (propDescriptor === undefined) {
                    continue
                }

                if (
                    typeof propDescriptor.value === 'function' ||
                    ('get' in propDescriptor && propDescriptor.get === undefined)
                ) {
                    continue
                }

                serializedProperties[camelToSnake(propName)] = (this as any)[propName]
            }

            target = Object.getPrototypeOf(target)
            if (target === Object.prototype) {
                break
            }
        }

        return serializedProperties
    }

    // This awful hack gets around a nasty circular dependency situtation I (Daniel) couldn't figure out.
    private static getSourceConstructor(): typeof Source {
        return (window as unknown as { PSSourceModel: typeof Source }).PSSourceModel
    }

    // A hack around a circular dependency situtation. See the comment in models/Transaction
    // about getLineConstructor for a simliar situtation.
    private static getImageConstructor(): typeof Image {
        return (window as unknown as { PSImageModel: typeof Image }).PSImageModel
    }

    jsonObject(): any {
        const dict: any = {}
        if (this.id !== undefined) dict['id'] = this.id
        return dict
    }

    static dataType(): DataType {
        throw new Error('Data type required for data access.')
    }

    // This function checks if there have been changes between the two passed in models
    // in any of the specified fields. Any fields that are base records are compared by
    // id only. It does a deep equality check on any array or object fields.
    static hasChanges<ModelT extends BaseRecord>(lhsModel: ModelT, rhsModel: ModelT): boolean {
        const areValuesInequal = (lhs: any, rhs: any): boolean => {
            if (moment.isMoment(lhs) && moment.isMoment(rhs)) {
                return !lhs.isSame(rhs)
            }
            else if (lhs instanceof BaseRecord && rhs instanceof BaseRecord) {
                return lhs.id !== rhs.id
            }
            else if (Array.isArray(lhs) && Array.isArray(rhs)) {
                if (lhs.length !== rhs.length) {
                    return true
                }
                return zip(lhs, rhs).some(([arrLhs, arrRhs]) => areValuesInequal(arrLhs, arrRhs))
            }
            else if (lhs === null || lhs === undefined || rhs === null || rhs === undefined) {
                return lhs !== rhs
            }
            else if (typeof lhs === 'object' && typeof rhs === 'object') {
                const lhsEntries = Object.entries(lhs)
                const rhsEntries = Object.entries(rhs)
                if (lhsEntries.length !== rhsEntries.length) {
                    return true
                }
                return lhsEntries.some(([key, lhsValue]) => areValuesInequal(lhsValue, rhs[key]))
            }
            else {
                return lhs !== rhs
            }
        }

        for (const fieldKey in lhsModel) {
            if (areValuesInequal(lhsModel[fieldKey], rhsModel[fieldKey])) {
                return true
            }
        }

        return false
    }

    equals(other: any): boolean {
        return other && this.id && other.constructor === this.constructor && other.id === this.id
    }

    /////////////////////////////////////////////////////////////////////////////////////////////////////////
    // The methods below this point are considered deprecated and should be avoided in newly written code  //
    //                                                                                                     //
    // The 4 methods below that have to do with serialization (serialize, deserialize, fromSerializer,     //
    // and serializeChildren) are used to pass records between components via location state. This         //
    // pattern of using location state is deprecated, and so these 4 methods that support that are also    //
    // deprecated.                                                                                         //
    //                                                                                                     //
    // The preferred alternative pattern is to have a single orchestrator component that renders various   //
    // subcomponents and owns the shared state for each of them.                                           //
    /////////////////////////////////////////////////////////////////////////////////////////////////////////
    public serialize() {
        return this.serializeChildren()
    }

    public deserialize(data: any): void {
        Object.entries(data)
            .map(([key, value]: [string, any]) => {
                if (value?.__ps_record_data_map__ === true || key === 'foreignKeyData') {
                    return [key, new RecordDataMap(omit(value, ['__ps_record_data_map__']))]
                }
                if (key === 'createdDate') {
                    return [key, value ? moment(value) : value]
                }
                return [key, value]
            })
            .forEach(([key, value]) => {
                try {
                    ;(this as any)[key] = value
                }
                catch {
                    // No-op
                }
            })
    }

    static fromSerializer<T extends BaseRecord>(data: any, model: new () => T) {
        const obj = new model()
        obj.deserialize(data)
        return obj
    }

    private serializeChildren(depth: number = 1) {
        const maxDepth = 5 // default max depth of 5 (arbirtary, should be plenty to get to any child & prevent infinite loops)
        if (depth > maxDepth) {
            return { id: this.id }
        }

        const valueOf = (value: any): any => {
            if (moment.isMoment(value)) {
                return moment(value).toISOString(true)
            }
            if (!!value?.$$typeof) {
                return undefined
            }
            if (value instanceof BaseRecord) {
                return (value as BaseRecord).serializeChildren(depth + 1)
            }
            if (value instanceof RecordDataMap) {
                const serializeRecordDataMapValue = (value: any): any => {
                    if (moment.isMoment(value)) {
                        return value.toISOString(true)
                    }
                    if (value instanceof BaseRecord) {
                        return value.id
                    }
                    if (value instanceof RecordDataMap) {
                        return valueOf(value)
                    }
                    if (isSerializable(value)) {
                        return (value as ISerializable).serialize()
                    }
                    return value
                }

                const serializedValues = mapValues(value, (value: any) => {
                    return isArray(value)
                        ? value.map(serializeRecordDataMapValue)
                        : serializeRecordDataMapValue(value)
                })
                return { ...serializedValues, __ps_record_data_map__: true }
            }
            if (isSerializable(value)) {
                return (value as ISerializable).serialize()
            }
            return value
        }

        const obj: any = {}

        let target = this
        const EXCLUDED_PROP_NAMES = [
            'iSerializable',
            'foreignKeyData',
            'foreignKeyDataCache',
            'recordType',
            'superClass',
        ]
        while (target !== null) {
            for (const propName of Object.getOwnPropertyNames(target)) {
                if (EXCLUDED_PROP_NAMES.includes(propName)) {
                    continue
                }

                const propDescriptor = Object.getOwnPropertyDescriptor(target, propName)
                if (propDescriptor === undefined) {
                    continue
                }

                if (
                    typeof propDescriptor.value === 'function' ||
                    ('get' in propDescriptor && propDescriptor.get === undefined)
                ) {
                    continue
                }

                const value = (this as any)[propName]
                if (isArray(value)) {
                    obj[propName] = value.map(valueOf)
                }
                else {
                    obj[propName] = valueOf(value)
                }
            }

            target = Object.getPrototypeOf(target)
            if (target === Object.prototype) {
                break
            }
        }

        for (const key in this) {
            if (EXCLUDED_PROP_NAMES.includes(key)) {
                continue
            }
            if (this[key] != undefined) {
                if (isArray(this[key])) {
                    obj[key] = (this[key] as any[])?.map(valueOf)
                }
                else {
                    obj[key] = valueOf(this[key])
                }
            }
        }

        return obj
    }
}
